import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Flex from '../../../components/Flex';
import { Form, Layout } from 'antd';
import { Button, Input, useTheme } from '@prio365/prio365-react-library';
import { colon } from '../../../util/forms';
import { PrioTheme } from '../../../theme/types';
import { Content } from 'antd/lib/layout/layout';
import { getSvgIconPathByType } from '../../../util/icon';
import SvgIcon from '../../../components/SvgIcon';
import {
  JiraConfiguration,
  defaultJiraConfiguration,
} from '../../../models/ProjectExtension';
import { makePrioStyles } from '../../../theme/utils';
import { apiCreateSetting } from '../api';

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  rootLayout: {
    margin: 0,
  },
  editContent: {
    backgroundColor: theme.colors.application.background.default,
  },
  form: {
    height: '100%',
    overflow: 'auto',
  },
  fullWidth: {
    width: '100%',
  },
  submitButton: {
    position: 'absolute',
    bottom: '32px',
  },
}));

interface JiramConfigProps {
  className?: string;
  projectExtensionConfigurationChanged: () => void;
  closeDrawer?: () => void;
}

export const JiraConfigurationCreate: React.FC<JiramConfigProps> = (props) => {
  //#region ------------------------------ Defaults
  const { projectExtensionConfigurationChanged, closeDrawer } = props;
  const { t } = useTranslation();
  const theme = useTheme<PrioTheme>();
  const classes = useStyles();
  const { projektExtensionId } = useParams();
  const [dataForm] = Form.useForm<JiraConfiguration>();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [isSaving, setIsSaving] = useState<boolean>(false);
  //#endregion

  //#region ------------------------------ Methods / Handlers

  // handle save
  const handleFinishData = async (newData: JiraConfiguration) => {
    const payload = {
      parentSettingId: '',
      jsonValue: JSON.stringify({
        email: newData.objectJson.serviceUserCredentials.mail,
        basePath: newData.objectJson.baseURL,
        apiToken: newData.objectJson.serviceUserCredentials.apiKey,
      }),
      relationId: '',
      isDefault: true,
      description: '',
    };

    setIsSaving(true);
    await apiCreateSetting(
      payload,
      'ProjectExtension_Jira_GlobalConnectionOptions'
    );

    projectExtensionConfigurationChanged();
    setIsSaving(false);
    closeDrawer && closeDrawer();
  };

  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    dataForm.setFieldsValue(defaultJiraConfiguration);
  }, [projektExtensionId, dataForm]);
  //#endregion

  return (
    <Layout className={classes.rootLayout}>
      <Content className={classes.editContent}>
        <>
          <Form
            layout="vertical"
            form={dataForm}
            className={classes.form}
            onFinish={handleFinishData}
          >
            <Flex.Row flex={1}>
              <SvgIcon
                width={35}
                height={35}
                path={getSvgIconPathByType('jira')}
              ></SvgIcon>
              <h3 style={{ marginTop: '3px', marginBottom: '32px' }}>
                Jira {t('settings:jiraConfig.connection')}
              </h3>
            </Flex.Row>
            <Flex.Row flex={1}>
              <Flex.Column flex={2} childrenGap={theme.spacing.small}>
                <Flex.Row
                  childrenGap={theme.spacing.small}
                  flex={1}
                  justifyContent="stretch"
                >
                  <Form.Item
                    name={['objectJson', 'baseURL']}
                    label="Jira URL"
                    colon={colon}
                    style={{ flex: 1 }}
                  >
                    <Input disabled={false} full />
                  </Form.Item>
                </Flex.Row>
                <Flex.Row
                  childrenGap={theme.spacing.small}
                  flex={1}
                  justifyContent="stretch"
                >
                  <Form.Item
                    name={['objectJson', 'serviceUserCredentials', 'mail']}
                    label="Mail"
                    colon={colon}
                    style={{ flex: 1 }}
                  >
                    <Input disabled={false} full />
                  </Form.Item>
                  <Form.Item
                    name={['objectJson', 'serviceUserCredentials', 'apiKey']}
                    label="API Token"
                    colon={colon}
                    style={{ flex: 1 }}
                  >
                    <Input disabled={false} full />
                  </Form.Item>
                </Flex.Row>
                <Flex.Row
                  childrenGap={theme.spacing.small}
                  flex={1}
                  justifyContent="flex-end"
                >
                  <Button
                    disabled={isSaving}
                    onClick={() => {
                      dataForm.submit();
                    }}
                    htmlType="button"
                    className={classes.submitButton}
                  >
                    {t('settings:jiraConfig.save')}
                  </Button>
                </Flex.Row>
              </Flex.Column>
            </Flex.Row>
          </Form>
        </>
      </Content>
    </Layout>
  );
};

export default JiraConfigurationCreate;
